/**
 * Per-user vs shared fields on a Space.
 *
 * The `entities` row for a Space is shared across every collaborator who has
 * read/write access. Anything written into `skeleton.windows`, `skeleton.settings`,
 * etc. via the REST `PUT /api/spaces/{id}` endpoint is visible to every other
 * collaborator the next time they open the space.
 *
 * Some Space state is intentionally per-user-per-space and lives in the per-user
 * Y.Doc room `space:{spaceId}:{userId}` (and, for a couple of fields, in
 * per-tab `localStorage`). It must NEVER be written into the shared row, or
 * collaborators will start seeing each other's tabs / chat tabs / scroll
 * positions / etc.
 *
 * This module is the single place that knows which fields are per-user and
 * strips them from any REST payload heading for the shared row. Every caller
 * that hits `spaceService.updateSpace` goes through `stripPerUserSpaceUpdate`.
 *
 * If you add a new per-user setting, add it here and nowhere else.
 */

import { createFileLogger } from './logger';

const log = createFileLogger('SPACE_SHARED_FIELDS', { enabled: true });

/**
 * Keys on `SpaceSettings` that must never be persisted to the shared backend
 * row. Each of these is sourced from a per-user surface (CRDT room or
 * localStorage).
 *
 * Why each one is per-user:
 *   - windowTabOrder        — tab ordering per the per-user `space:` Y.Doc
 *   - sidebar               — sidebar visibility/width is per-user UI prefs
 *   - entitySidebar         — folder navigation state is per-user
 *
 * Note: `spaceChat` has its own per-user sub-fields handled below.
 */
const TOP_LEVEL_PER_USER_SETTING_KEYS = [
  'windowTabOrder',
  'sidebar',
  'entitySidebar',
] as const;

/**
 * Sub-keys on `settings.spaceChat` that are per-user. The `spaceChat` object
 * itself is mostly shared (model, isHistoryOpen, widths, etc.), but tab and
 * thread state is private.
 */
const PER_USER_SPACE_CHAT_KEYS = [
  'tabs',
  'activeTabId',
  'currentThreadIds',
] as const;

/**
 * Return a copy of `settings` with every per-user field removed. Safe to send
 * via `PUT /api/spaces/{id}`.
 *
 * The backend deep-merges settings, so omitting a key leaves whatever was
 * previously stored intact — this is the desired behaviour for shared fields
 * we don't own and necessary for per-user fields we don't want to leak.
 */
export function stripPerUserSettings<T extends Record<string, any> | null | undefined>(
  settings: T,
): T {
  if (!settings || typeof settings !== 'object') return settings;

  const next: Record<string, any> = { ...settings };

  for (const key of TOP_LEVEL_PER_USER_SETTING_KEYS) {
    if (key in next) delete next[key];
  }

  if (next.spaceChat && typeof next.spaceChat === 'object') {
    const sharedSpaceChat = { ...next.spaceChat };
    for (const key of PER_USER_SPACE_CHAT_KEYS) {
      if (key in sharedSpaceChat) delete sharedSpaceChat[key];
    }
    next.spaceChat = sharedSpaceChat;
  }

  return next as T;
}

/**
 * Shape of the payload going to `PUT /api/spaces/{id}`. Mirrors the fields
 * `spaceService.updateSpace` historically accepted.
 */
export interface SpaceUpdatePayloadInput {
  windows?: Record<string, any> | null;
  settings?: Record<string, any> | null;
  activeWindowId?: string | null;
  contextEntities?: Record<string, any> | null;
  contextExcerpts?: any[] | null;
}

export interface SpaceUpdatePayloadOutput {
  settings?: Record<string, any>;
  contextEntities?: Record<string, any>;
  contextExcerpts?: any[];
}

/**
 * Sanitize a Space-update payload before it leaves the client.
 *
 * Drops fields that are per-user-per-space and must not enter the shared
 * MongoDB row:
 *   - `windows`         — owned by the per-user `space:{spaceId}:{userId}` Y.Doc
 *   - `activeWindowId`  — per-tab in localStorage; legacy shared field is ignored
 *   - per-user keys inside `settings` (see `stripPerUserSettings`)
 *
 * `contextEntities` / `contextExcerpts` are passed through unchanged for now
 * (still shared today), but the strip site is centralized here so it's a
 * one-line change to make them per-user later.
 */
export function stripPerUserSpaceUpdate(
  input: SpaceUpdatePayloadInput,
  callsite?: string,
): SpaceUpdatePayloadOutput {
  const out: SpaceUpdatePayloadOutput = {};

  // `windows` is per-user CRDT state and must never enter the shared row.
  // The legacy `skeleton.windows` field on existing spaces is preserved
  // untouched by the backend (it only updates fields present on the request).
  if (input.windows !== undefined && input.windows !== null) {
    log.log(
      `[stripPerUserSpaceUpdate] dropping per-user windows from REST payload (${Object.keys(input.windows).length} entries) — callsite=${callsite ?? 'unknown'}`,
    );
  }

  // `activeWindowId` is per-tab; localStorage owns it.
  if (input.activeWindowId !== undefined && input.activeWindowId !== null) {
    log.log(
      `[stripPerUserSpaceUpdate] dropping per-tab activeWindowId from REST payload — callsite=${callsite ?? 'unknown'}`,
    );
  }

  if (input.settings !== undefined && input.settings !== null) {
    out.settings = stripPerUserSettings(input.settings);
  }

  if (input.contextEntities !== undefined && input.contextEntities !== null) {
    out.contextEntities = input.contextEntities as Record<string, any>;
  }

  if (input.contextExcerpts !== undefined && input.contextExcerpts !== null) {
    out.contextExcerpts = input.contextExcerpts;
  }

  return out;
}
